import Button from 'components/Form/Button';
import BasicTable from 'components/Form/Table';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { Read } from 'services/financing-proposals';
import { StatusContent } from 'ui/styled';
import { exposeStrapiError, marcadorDiferente } from 'utils';
import { formatElapsedTime, parseCurrency } from 'utils/parsers';
import { formatadores, normalizeName } from 'utils/validators';

export default function FinancingDeskApproved() {
  const history = useHistory();
  const navigate = useCallback((to) => history.push(`/${to}`), [history]);
  const { user, filterExpression, setModal } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const [registers, setRegisters] = useState([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick((prev) => prev + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: 'Id',
        sortable: true,
        ref: 'id',
        renderCell: ({ row }) => {
          // Condição: Se o campo 'curadoria_analise' for 'Analisado'
          const isAnalisado = row?.curadoria_analise === 'Analisado';
          const id = row?.id;
          if (isAnalisado) {
            return marcadorDiferente(id, '!');
          }
          return id;
        },
      },
      {
        title: 'Data',
        sortable: true,
        ref: 'date',
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_BR(row?.date),
      },
      {
        title: 'Tempo',
        renderCell: ({ row }) => {
          const diffDays = formatadores.DIFF_DAYS_NOW(row?.date);
          return (
            <span style={{ color: diffDays >= 2 ? 'red' : 'inherit' }}>
              {formatElapsedTime(row?.date)}
            </span>
          );
        },
      },
      { title: 'Proposta', ref: 'cod_proposta_externa' },
      ...(user?.administrador ||
      (user?.distribuidor && user?.distribuidores?.length > 0)
        ? [{ title: 'Distribuidora', sortable: true, ref: 'distributor' }]
        : []),
      { title: 'Integrador', sortable: true, ref: 'integrator' },
      { title: 'Cliente', sortable: true, ref: 'customer' },
      { title: 'Valor', ref: 'value' },
      { title: 'Banco', sortable: true, ref: 'banco' },
      { title: 'parcelas', ref: 'installments' },
      { title: 'valor da PMT', ref: 'installments_value' },
      {
        title: '1º Vencimento',
        ref: 'due_date',
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_BR(row.due_date),
      },
      {
        title: 'TAG',
        ref: 'tag',
        renderCell: () => {
          const tagText = 'Aguardando Autorização do Vendedor';
          const tagColor = 'orange';
          return <StatusContent color={tagColor}>{tagText}</StatusContent>;
        },
      },
      ...(user?.administrador
        ? [
            {
              title: 'Espelho da Proposta',
              ref: 'espelho',
              renderCell: ({ row }) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    onClick={() => navigate(`proposals/financing/${row?.id}`)}
                    color="blue"
                    nospace
                    mbutton
                    small
                  >
                    Visualizar
                  </Button>
                </div>
              ),
            },
            {
              title: 'Observações',
              renderCell: ({ row }) => (
                <Button
                  onClick={() => {
                    setModal({
                      type: 'observacao',
                      dados: row,
                      origem: 'Aprovado',
                      reload: () => init(),
                    });
                  }}
                  color="botao"
                  nospace
                  mbutton
                  small
                >
                  Adicionar
                </Button>
              ),
            },
          ]
        : []),
    ].filter((f) => f);
    return baseColumns;
  }, [navigate, user]);
  const rows = useMemo(() => {
    return registers?.map((item) => {
      return {
        id: item?.id,
        date: item?.data_aprovada,
        formatedDate: formatadores.TIMESTAMP_TO_BR(item?.data_aprovada),
        cod_proposta_externa: normalizeName(item?.cod_proposta_externa),
        distributor: normalizeName(item?.distribuidor?.trade_name),
        integrator: normalizeName(item?.integrador?.nome_fantasia),
        seller: normalizeName(item?.informacoes_projeto?.nome_vendedor),
        telephone: item?.informacoes_cliente?.telefone_celular,
        customer: normalizeName(item?.informacoes_cliente?.nome_completo),
        value: parseCurrency(item?.informacoes_projeto?.custo_total_projeto),
        status: normalizeName(item?.status),
        banco: normalizeName(item?.bank?.banco?.nome_fantasia),
        installments: item?.bank?.prazo_financiamento,
        installments_value: parseCurrency(item?.bank?.valor_parcelas),
        due_date: !item?.bank?.data ? '' : item?.bank?.data,
        curadoria_analise: item?.curadoria_analise,
        curadoria_analise_bot: item?.curadoria_analise_bot,
        cnpj_integrador: item?.integrador?.cnpj,
        cnpj_integrador_clean: item?.integrador?.cnpj.replace(/\D/g, ''),
        cpf_cnpj: item?.cliente?.cpf || item?.cliente?.cnpj,
        cpf_cnpj_clean: item?.cliente?.cpf
          ? item?.cliente?.cpf.replace(/\D/g, '')
          : item?.cliente?.cnpj.replace(/\D/g, ''),
      };
    });
  }, [registers]);

  const init = useCallback(async () => {
    setLoading(true);
    const result = await Read('?filters[status]=aprovado');
    setLoading(false);
    if (result && !exposeStrapiError(result)) {
      const nresult = result?.results?.sort((a, b) => b.id - a.id);
      const nextResult = nresult
        ?.reduce(
          (p, m) => [
            ...p,
            m?.propostas_banco
              ?.filter((f) => f.status === 'aprovado')
              ?.map((mm) => ({ ...m, bank: mm })),
          ],
          []
        )
        ?.reduce((p, c) => [...p, ...c], []);
      setRegisters(nextResult);
    }
  }, []);

  useEffect(() => init(), [tick, init]);

  return (
    <ContainerAuthenticated>
      <TableWrapper title={'Aprovadas'} search>
        <BasicTable
          loading={loading}
          columns={columns}
          rows={rows?.filter(filterExpression)}
          pagination
        />
      </TableWrapper>
    </ContainerAuthenticated>
  );
}
